import React from 'react';

import { CHART_BAR } from '../icons';
import { DASHBOARD_SCOPE } from '../../services/dashboards';
import { toShortPath } from '../../utils/recordCodes';
import { siteOrigin } from '../../platform';

/**
 * A dashboard's actions, in the platform taxonomy (standards §3; plan §6.1,
 * §6.5): the Reports row's ⋯ and the dashboard page's ⋯ are built from this
 * one list (the availability rule). A surface passes only the handlers that
 * make sense where it is: the row has Open, the page has Go to Reports and
 * Print… instead, and the page's Arrange is a labelled button, not an entry.
 *
 *   Go to            Open · Go to Reports
 *   Record           Rename… · Duplicate… · Pin to Home · Show on my Overview |
 *                    Use the collection's Overview · Show on Overview for
 *                    everyone · Pin for everyone | Unpin — return to your own list
 *   Share & export   Copy link · Print…
 *   Danger           Delete…
 *
 * Who may do what is the server's answer: `dashboard.canEdit` (its creator
 * for a personal one, a manager for one pinned for everyone) gates Rename
 * and Delete; pinning and unpinning are a
 * manager's, on this collection's own dashboards. Duplicate makes a personal
 * copy, which anyone who reads the dashboard may.
 *
 * Overview and Home (charts A7, ruled q04 and q16): **Pin to Home** puts the
 * dashboard on the person's Home as a launcher (`pin` glyph); **Show on my
 * Overview** makes it what this collection's Overview shows them, and, when
 * it already does, **Use the collection's Overview** goes back; a manager's
 * **Show on Overview for everyone** (`collection` glyph) is offered only for a
 * dashboard pinned for everyone (its own or an ancestor's) that the
 * collection does not already show.
 */
export function dashboardActions({
	dashboard,
	isManager = false,
	onOpen,
	onReports,
	onRename,
	onDuplicate,
	pinnedHome = false,
	onPinHome,
	isMyOverview = false,
	onShowOnMyOverview,
	onUseCollectionOverview,
	isCollectionOverview = false,
	onShowOnOverviewForEveryone,
	onPromote,
	onDemote,
	onCopyLink,
	onPrint,
	onDelete,
}) {
	if (!dashboard) return [];
	const forEveryone = dashboard.scope === DASHBOARD_SCOPE.COLLECTION;
	const mayEdit = dashboard.canEdit === true;
	return [
		onOpen ? { key: 'open', group: 'navigate', glyph: 'open', label: 'Open', onClick: onOpen } : null,
		onReports ? { key: 'reports', group: 'navigate', icon: <CHART_BAR />, label: 'Go to Reports', onClick: onReports } : null,
		onRename && mayEdit ? { key: 'rename', group: 'record', glyph: 'edit', label: 'Rename…', onClick: onRename } : null,
		onDuplicate ? { key: 'duplicate', group: 'record', glyph: 'duplicate', label: 'Duplicate…', onClick: onDuplicate } : null,
		onPinHome ? { key: 'pin-home', group: 'record', glyph: 'pin', label: pinnedHome ? 'Unpin from Home' : 'Pin to Home', onClick: onPinHome } : null,
		onShowOnMyOverview && !isMyOverview ? { key: 'my-overview', group: 'record', glyph: 'home', label: 'Show on my Overview', onClick: onShowOnMyOverview } : null,
		onUseCollectionOverview && isMyOverview ? { key: 'collection-overview', group: 'record', glyph: 'home', label: 'Use the collection\'s Overview', onClick: onUseCollectionOverview } : null,
		onShowOnOverviewForEveryone && isManager && forEveryone && !isCollectionOverview ? {
			key: 'overview-everyone', group: 'record', glyph: 'collection', label: 'Show on Overview for everyone', onClick: onShowOnOverviewForEveryone,
		} : null,
		onPromote && isManager && !forEveryone ? {
			key: 'promote', group: 'record', glyph: 'collection', label: 'Pin for everyone', onClick: onPromote,
		} : null,
		onDemote && isManager && forEveryone ? {
			key: 'demote', group: 'record', glyph: 'collection', label: 'Unpin — return to your own list', onClick: onDemote,
		} : null,
		onCopyLink ? { key: 'copy-link', group: 'share', glyph: 'share', label: 'Copy link', onClick: onCopyLink } : null,
		onPrint ? { key: 'print', group: 'share', glyph: 'print', label: 'Print…', onClick: onPrint } : null,
		onDelete && mayEdit ? { key: 'delete', group: 'destructive', glyph: 'delete', label: 'Delete…', onClick: onDelete } : null,
	].filter(Boolean);
}

/** The words of a dashboard's delete confirmation: what goes, for whom, what stays, and that History brings it back. */
export function deleteDashboardCopy(dashboard) {
	const name = dashboard && typeof dashboard.name === 'string' && dashboard.name ? dashboard.name : 'this dashboard';
	return {
		title: `Delete "${name}"?`,
		content: dashboard && dashboard.scope === DASHBOARD_SCOPE.COLLECTION
			? 'It disappears for everyone who uses this collection. Its charts stay. History can restore it.'
			: 'Only you can see this dashboard, so only you lose it. Its charts stay. History can restore it.',
	};
}

/** A dashboard's address to copy: this site's own, with what the page asks (the controls in its URL). */
export const absoluteUrl = (pathAndSearch) => {
	const origin = siteOrigin();
	// In its short form when the collection's code is known (/c/<code>/dashboards/…).
	return `${origin}${toShortPath(pathAndSearch)}`;
};
